import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Chip,
  Stack,
  Autocomplete,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
  Pagination,
} from '@mui/material';
import { OpenInNew, Check, Warning } from '@mui/icons-material';
import api from '../services/api';
import type { Product } from '../types';
import TextField from '../components/common/TextField';

interface MlSuggestion {
  productId: number;
  productName: string;
  sku?: string;
  score: number;
}

interface MlPublication {
  id: string;
  title: string;
  permalink?: string;
  price?: number;
  sku?: string;
  userProductId?: string;
  mlStock?: number;
  mlFullStock?: number;
  variations?: { id?: string; sku?: string; available?: number }[];
  mappings?: { productId: number; productName: string; externalSku?: string; quantity?: number }[];
  suggestions?: MlSuggestion[];
}

export const MlMappingPage: React.FC = () => {
  const [channels, setChannels] = useState<any[]>([]);
  const [selectedChannel, setSelectedChannel] = useState<number | null>(null);
  const [publications, setPublications] = useState<MlPublication[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selections, setSelections] = useState<Record<string, { productId: number; externalSku?: string; quantity: number }[]>>({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPublications, setTotalPublications] = useState(0);
  const [fetchingAll, setFetchingAll] = useState(false);
  const [relinkPast, setRelinkPast] = useState(true);
  const [relinkedTotal, setRelinkedTotal] = useState(0);
  const pageSize = 50;

  useEffect(() => {
    loadChannels();
    loadProducts();
  }, []);

  const loadChannels = async () => {
    try {
      const res = await api.getChannels();
      const mlChannels = (res.items || []).filter((c: any) => c.type === 'MercadoLibre' && c.isConnected);
      setChannels(mlChannels);
      if (mlChannels.length === 1) {
        setSelectedChannel(mlChannels[0].id);
      }
    } catch {
      setError('Error cargando canales');
    }
  };

  const loadProducts = async () => {
    try {
      const res = await api.getProducts({ page: 1, pageSize: 500 });
      setProducts(res.products || []);
    } catch {
      setProducts([]);
    }
  };

  useEffect(() => {
    if (selectedChannel) {
      loadPublications(1);
    }
  }, [selectedChannel]);

  const loadPublications = async (pageNum: number, fetchAll = false) => {
    if (!selectedChannel) return;
    setLoading(true);
    if (fetchAll) setFetchingAll(true);
    setError(null);
    try {
      const offset = fetchAll ? 0 : (pageNum - 1) * pageSize;
      const res = await api.getMlPublications(selectedChannel, { limit: pageSize, offset, fetchAll });
      setPublications(res.items || []);
      const total = res.paging?.total || res.items?.length || 0;
      setTotalPublications(total);
      setTotalPages(Math.ceil(total / pageSize) || 1);
      setPage(fetchAll ? 1 : pageNum);

      // Initialize selections from existing mappings
      const initialSelections: Record<string, { productId: number; externalSku?: string; quantity: number }[]> = {};
      (res.items || []).forEach((p: MlPublication) => {
        if (p.mappings && Array.isArray(p.mappings)) {
          initialSelections[p.id] = p.mappings.map((m) => ({ productId: m.productId, externalSku: m.externalSku, quantity: m.quantity || 1 }));
        }
      });
      setSelections(initialSelections);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error cargando publicaciones');
      setPublications([]);
    } finally {
      setLoading(false);
      setFetchingAll(false);
    }
  };

  const loadAllPublications = async () => {
    await loadPublications(1, true);
  };

  const handleSelectionChange = (pubId: string, selectedProducts: Product[]) => {
    setSelections((prev) => {
      const existing = prev[pubId] || [];
      return {
        ...prev,
        [pubId]: selectedProducts.map((p) => {
          const existingItem = existing.find((e) => e.productId === p.id);
          return { productId: p.id, externalSku: p.sku, quantity: existingItem?.quantity || 1 };
        }),
      };
    });
  };

  const handleQuantityChange = (pubId: string, productId: number, quantity: number) => {
    setSelections((prev) => ({
      ...prev,
      [pubId]: (prev[pubId] || []).map((item) =>
        item.productId === productId ? { ...item, quantity: Math.max(1, quantity) } : item
      ),
    }));
  };

  const saveMapping = async (pubId: string, userProductId?: string) => {
    if (!selectedChannel) return;
    setSaving(true);
    try {
      const res = await api.mapMlPublication({
        channelId: selectedChannel,
        externalId: pubId,
        mappings: selections[pubId] || [],
        relinkPast,
      });
      if (res?.relinked) setRelinkedTotal((n) => n + res.relinked!);
      // Refresh to show updated mappings
      await loadPublications(page);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error guardando mapeo');
    } finally {
      setSaving(false);
    }
  };

  const saveAllMappings = async () => {
    if (!selectedChannel) return;
    setSaving(true);
    let relinkedAcc = 0;
    try {
      for (const pub of publications) {
        if (selections[pub.id] && selections[pub.id].length > 0) {
          const res = await api.mapMlPublication({
            channelId: selectedChannel,
            externalId: pub.id,
            mappings: selections[pub.id],
            relinkPast,
          });
          if (res?.relinked) relinkedAcc += res.relinked;
        }
      }
      if (relinkedAcc > 0) setRelinkedTotal((n) => n + relinkedAcc);
      await loadPublications(page);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error guardando mapeos');
    } finally {
      setSaving(false);
    }
  };

  const selectedChannelData = channels.find((c) => c.id === selectedChannel);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
        Mapeo de Publicaciones MercadoLibre
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Vinculá tus publicaciones de MercadoLibre con los productos de tu catálogo para sincronizar órdenes automáticamente.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {channels.length === 0 && !loading && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No hay canales de MercadoLibre conectados. Conectá un canal desde la página de Canales.
        </Alert>
      )}

      {channels.length > 1 && (
        <Autocomplete
          options={channels}
          getOptionLabel={(c) => `${c.name} (${c.accountInfo?.nickname || c.accountInfo?.userId || 'ML'})`}
          value={channels.find((c) => c.id === selectedChannel) || null}
          onChange={(_, val) => setSelectedChannel(val?.id || null)}
          renderInput={(params) => <TextField {...params} label="Seleccionar canal" />}
          sx={{ mb: 3, maxWidth: 400 }}
        />
      )}

      {selectedChannel && selectedChannelData && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="subtitle2">
              Canal: <strong>{selectedChannelData.name}</strong>
            </Typography>
            {selectedChannelData.accountInfo?.nickname && (
              <Chip size="small" label={selectedChannelData.accountInfo.nickname} />
            )}
            {selectedChannelData.accountInfo?.siteId && (
              <Chip size="small" variant="outlined" label={selectedChannelData.accountInfo.siteId} />
            )}
          </Stack>
        </Paper>
      )}

      {loading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
          <CircularProgress />
          {fetchingAll && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Cargando todas las publicaciones... Esto puede tardar unos segundos.
            </Typography>
          )}
        </Box>
      )}

      {!loading && selectedChannel && publications.length === 0 && (
        <Alert severity="info">No se encontraron publicaciones en este canal.</Alert>
      )}

      {!loading && publications.length > 0 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="subtitle1">
                {publications.length} de {totalPublications} publicaciones
              </Typography>
              {totalPublications > publications.length && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={loadAllPublications}
                  disabled={loading || fetchingAll}
                >
                  {fetchingAll ? 'Cargando...' : `Cargar todas (${totalPublications})`}
                </Button>
              )}
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Stack direction="row" spacing={0.5} alignItems="center">
                <input
                  id="relink-past"
                  type="checkbox"
                  checked={relinkPast}
                  onChange={(e) => setRelinkPast(e.target.checked)}
                />
                <Typography variant="body2" component="label" htmlFor="relink-past" sx={{ cursor: 'pointer' }}>
                  Re-mapear órdenes previas (del placeholder)
                </Typography>
              </Stack>
              <Button
                variant="contained"
                onClick={saveAllMappings}
                disabled={saving}
              >
                {saving ? 'Guardando...' : 'Guardar todos los mapeos'}
              </Button>
            </Stack>
          </Box>

          {relinkedTotal > 0 && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRelinkedTotal(0)}>
              Re-mapeadas {relinkedTotal} órdenes previas al producto real (stock no se modificó).
            </Alert>
          )}

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width="30%">Publicación ML</TableCell>
                  <TableCell width="10%">SKU ML</TableCell>
                  <TableCell width="10%" align="center">Stock ML</TableCell>
                  <TableCell width="40%">Producto Local (con cantidad)</TableCell>
                  <TableCell width="10%" align="center">Estado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {publications.map((pub) => {
                  const currentSelections = selections[pub.id] || [];
                  const isMapped = currentSelections.length > 0;
                  const selectedProductIds = currentSelections.map((s) => s.productId);

                  return (
                    <TableRow key={pub.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {pub.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {pub.id}
                        </Typography>
                        {pub.permalink && (
                          <Link
                            href={pub.permalink}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ ml: 1, display: 'inline-flex', alignItems: 'center' }}
                          >
                            <OpenInNew sx={{ fontSize: 12 }} />
                          </Link>
                        )}
                        {pub.variations && pub.variations.length > 0 && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Variantes: {pub.variations.map((v) => v.sku || v.id).filter(Boolean).join(', ') || '-'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{pub.sku || '-'}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Stack spacing={0.5} alignItems="center">
                          {pub.variations && pub.variations.length > 0 ? (
                            pub.variations.map((v, idx) => (
                              <Typography key={idx} variant="caption" color="text.secondary">
                                {v.sku || v.id}: {v.available ?? '—'}
                              </Typography>
                            ))
                          ) : (
                            <Typography variant="body2">
                              {pub.mlStock ?? '—'}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Autocomplete
                          multiple
                          size="small"
                          options={products}
                          getOptionLabel={(p) => `${p.sku} - ${p.name}`}
                          value={products.filter((p) => selectedProductIds.includes(p.id))}
                          onChange={(_, vals) => handleSelectionChange(pub.id, vals)}
                          renderInput={(params) => (
                            <TextField {...params} placeholder="Buscar producto..." />
                          )}
                          sx={{ minWidth: 250 }}
                        />
                        {currentSelections.length === 0 && pub.suggestions && pub.suggestions.length > 0 && (
                          <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                              Sugerencias:
                            </Typography>
                            {pub.suggestions.map((s) => {
                              const prod = products.find((p) => p.id === s.productId);
                              if (!prod) return null;
                              return (
                                <Chip
                                  key={s.productId}
                                  size="small"
                                  label={`${prod.name} (${Math.round(s.score * 100)}%)`}
                                  onClick={() => handleSelectionChange(pub.id, [prod])}
                                  variant="outlined"
                                  color="primary"
                                  sx={{ cursor: 'pointer' }}
                                />
                              );
                            })}
                          </Stack>
                        )}
                        {currentSelections.length > 0 && (
                          <Stack spacing={1} sx={{ mt: 1 }}>
                            {currentSelections.map((sel) => {
                              const prod = products.find((p) => p.id === sel.productId);
                              return (
                                <Stack key={sel.productId} direction="row" alignItems="center" spacing={1}>
                                  <Typography variant="caption" sx={{ flex: 1, minWidth: 120 }}>
                                    {prod?.name || `ID ${sel.productId}`}
                                  </Typography>
                                  <TextField
                                    type="number"
                                    size="small"
                                    label="Cant."
                                    value={sel.quantity}
                                    onChange={(e) => handleQuantityChange(pub.id, sel.productId, parseInt(e.target.value) || 1)}
                                    inputProps={{ min: 1, style: { width: 50 } }}
                                    sx={{ width: 80 }}
                                  />
                                </Stack>
                              );
                            })}
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {isMapped ? (
                          <Chip
                            size="small"
                            icon={<Check />}
                            label="Mapeado"
                            color="success"
                            variant="outlined"
                          />
                        ) : (
                          <Chip
                            size="small"
                            icon={<Warning />}
                            label="Sin mapear"
                            color="warning"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, p) => loadPublications(p)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};
